import { account, databases, client, DATABASE_ID, COLLECTIONS, Query, ID } from '../lib/appwrite';

// OPTIMIZATION: Add simple in-memory cache with 30-second TTL
const cache = {
  data: null,
  timestamp: null,
  ttl: 30000 // 30 seconds
};

/**
 * Service for managing dewars with Appwrite integration
 * Handles snake_case ↔ camelCase conversion between database and React
 */

export const dewarService = {
  async getAll() {
    try {
      // OPTIMIZATION: Check cache first
      const now = Date.now();
      if (cache.data && cache.timestamp && (now - cache.timestamp) < cache.ttl) {
        console.log('Returning cached dewars data');
        return cache.data;
      }

      // Fetch all dewars
      const dewarsResponse = await databases.listDocuments(
        DATABASE_ID,
        COLLECTIONS.DEWARS,
        [
          Query.orderDesc('$createdAt'),
          Query.limit(100)
        ]
      );

      // Fetch all related data in parallel for better performance
      const transformed = await Promise.all(
        dewarsResponse.documents.map(async (dewar) => {
          // Fetch racks for this dewar
          const racksResponse = await databases.listDocuments(
            DATABASE_ID,
            COLLECTIONS.RACKS,
            [
              Query.equal('dewar_id', dewar.$id),
              Query.orderAsc('rack_number'),
              Query.limit(10)
            ]
          );

          // Fetch nitrogen history for this dewar
          const historyResponse = await databases.listDocuments(
            DATABASE_ID,
            COLLECTIONS.NITROGEN_LEVEL_HISTORY,
            [
              Query.equal('dewar_id', dewar.$id),
              Query.orderDesc('checked_at'),
              Query.limit(50)
            ]
          );

          // Fetch boxes and samples for each rack
          const racks = await Promise.all(
            racksResponse.documents.map(async (rack) => {
              const boxesResponse = await databases.listDocuments(
                DATABASE_ID,
                COLLECTIONS.BOXES,
                [
                  Query.equal('rack_id', rack.$id),
                  Query.limit(15)
                ]
              );

              // Fetch samples for each box
              const boxes = await Promise.all(
                boxesResponse.documents.map(async (box) => {
                  const samplesResponse = await databases.listDocuments(
                    DATABASE_ID,
                    COLLECTIONS.SAMPLES,
                    [
                      Query.equal('box_id', box.$id),
                      Query.limit(100)
                    ]
                  );

                  return {
                    id: box.$id,
                    name: box.name,
                    type: box.box_type,
                    occupied: box.occupied_count || 0,
                    capacity: box.box_type === '9x9' ? 81 : 100,
                    rackId: box.rack_id,
                    createdAt: box.$createdAt,
                    samples: samplesResponse.documents.map(sample => ({
                      id: sample.$id,
                      sampleId: sample.sample_id,
                      name: sample.name,
                      position: sample.position,
                      rowIndex: sample.row_index,
                      colIndex: sample.col_index,
                      freezeDate: sample.freeze_date,
                      frozenBy: sample.frozen_by,
                      pi: sample.principal_investigator,
                      passageNumber: sample.passage_number,
                      notes: sample.notes,
                      capColor: sample.cap_color,
                      createdAt: sample.$createdAt
                    }))
                  };
                })
              );

              return {
                id: rack.$id,
                name: rack.name,
                number: rack.rack_number,
                dewarId: rack.dewar_id,
                boxes
              };
            })
          );

          return {
            id: dewar.$id,
            name: dewar.name,
            location: dewar.location,
            temp: dewar.temperature,
            status: dewar.status,
            liquidNitrogenLevel: dewar.liquid_nitrogen_level,
            createdAt: dewar.$createdAt,
            updatedAt: dewar.$updatedAt,
            levelHistory: historyResponse.documents.map(log => ({
              id: log.$id,
              date: log.checked_at,
              level: parseFloat(log.level),
              previousLevel: log.previous_level ? parseFloat(log.previous_level) : null,
              checkedBy: log.checked_by,
              action: log.action,
              notes: log.notes
            })),
            racks
          };
        })
      );

      // OPTIMIZATION: Update cache
      cache.data = transformed;
      cache.timestamp = now;

      return transformed;
    } catch (error) {
      console.error('Error fetching dewars:', error);
      throw error;
    }
  },

  // OPTIMIZATION: Add cache invalidation method
  invalidateCache() {
    cache.data = null;
    cache.timestamp = null;
  },

  /**
   * Subscribe to realtime changes for dewars
   * @param {Function} callback - Function to call when data changes
   * @returns {Function} unsubscribe function
   */
  subscribe(callback) {
    const channels = [
      `databases.${DATABASE_ID}.collections.${COLLECTIONS.DEWARS}.documents`,
      `databases.${DATABASE_ID}.collections.${COLLECTIONS.RACKS}.documents`,
      `databases.${DATABASE_ID}.collections.${COLLECTIONS.BOXES}.documents`,
      `databases.${DATABASE_ID}.collections.${COLLECTIONS.SAMPLES}.documents`
    ];

    const unsubscribe = client.subscribe(channels, async (response) => {
      console.log('Realtime event received:', response.events);
      // Invalidate cache and reload data
      this.invalidateCache();
      const data = await this.getAll();
      callback(data);
    });

    // Return unsubscribe function
    return unsubscribe;
  },

  /**
   * Create a new dewar
   */
  async create(dewarData) {
    try {
      const user = await account.get();
      if (!user) throw new Error('Not authenticated');

      // Create dewar
      const dewar = await databases.createDocument(
        DATABASE_ID,
        COLLECTIONS.DEWARS,
        ID.unique(),
        {
          user_id: user.$id,
          name: dewarData.name,
          location: dewarData.location || '',
          temperature: dewarData.temp || '-196°C',
          status: dewarData.status || 'Operational',
          liquid_nitrogen_level: parseFloat(dewarData.liquidNitrogenLevel || 25)
        }
      );

      // Create initial nitrogen level history entry
      await databases.createDocument(
        DATABASE_ID,
        COLLECTIONS.NITROGEN_LEVEL_HISTORY,
        ID.unique(),
        {
          dewar_id: dewar.$id,
          level: parseFloat(dewarData.liquidNitrogenLevel || 25),
          previous_level: null,
          checked_by: 'System',
          action: 'Initial Setup',
          notes: 'Dewar created with initial nitrogen level',
          checked_at: new Date().toISOString()
        }
      );

      // Create 6 racks with 10 boxes each
      for (let rackNum = 1; rackNum <= 6; rackNum++) {
        const rack = await databases.createDocument(
          DATABASE_ID,
          COLLECTIONS.RACKS,
          ID.unique(),
          {
            dewar_id: dewar.$id,
            rack_number: rackNum,
            name: `Rack ${rackNum}`
          }
        );

        // Create 10 boxes for this rack (1A-1J, 2A-2J, etc.)
        const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

        for (const letter of letters) {
          await databases.createDocument(
            DATABASE_ID,
            COLLECTIONS.BOXES,
            ID.unique(),
            {
              rack_id: rack.$id,
              name: `${rackNum}${letter}`,
              box_type: '9x9',
              max_capacity: 81,
              occupied_count: 0
            }
          );
        }
      }

      // CRITICAL FIX: Invalidate cache so next getAll() fetches fresh data
      this.invalidateCache();

      // Return full dewar data
      return this.getById(dewar.$id);
    } catch (error) {
      console.error('Error creating dewar:', error);
      throw error;
    }
  },

  /**
   * Get single dewar by ID
   */
  async getById(dewarId) {
    try {
      // Fetch dewar
      const dewar = await databases.getDocument(
        DATABASE_ID,
        COLLECTIONS.DEWARS,
        dewarId
      );

      // Fetch racks
      const racksResponse = await databases.listDocuments(
        DATABASE_ID,
        COLLECTIONS.RACKS,
        [
          Query.equal('dewar_id', dewarId),
          Query.orderAsc('rack_number'),
          Query.limit(10)
        ]
      );

      // Fetch nitrogen history
      const historyResponse = await databases.listDocuments(
        DATABASE_ID,
        COLLECTIONS.NITROGEN_LEVEL_HISTORY,
        [
          Query.equal('dewar_id', dewarId),
          Query.orderDesc('checked_at'),
          Query.limit(50)
        ]
      );

      // Fetch boxes for each rack
      const racks = await Promise.all(
        racksResponse.documents.map(async (rack) => {
          const boxesResponse = await databases.listDocuments(
            DATABASE_ID,
            COLLECTIONS.BOXES,
            [
              Query.equal('rack_id', rack.$id),
              Query.limit(15)
            ]
          );

          return {
            id: rack.$id,
            number: rack.rack_number,
            name: rack.name,
            boxes: boxesResponse.documents.map(box => ({
              id: box.$id,
              name: box.name,
              type: box.box_type,
              samples: box.max_capacity,
              occupied: box.occupied_count
            }))
          };
        })
      );

      return {
        id: dewar.$id,
        name: dewar.name,
        location: dewar.location,
        temp: dewar.temperature,
        status: dewar.status,
        liquidNitrogenLevel: parseFloat(dewar.liquid_nitrogen_level),
        levelHistory: historyResponse.documents.map(log => ({
          id: log.$id,
          date: log.checked_at,
          level: parseFloat(log.level),
          previousLevel: log.previous_level ? parseFloat(log.previous_level) : null,
          checkedBy: log.checked_by,
          action: log.action,
          notes: log.notes
        })),
        racks
      };
    } catch (error) {
      console.error('Error fetching dewar by ID:', error);
      throw error;
    }
  },

  /**
   * Update dewar information
   */
  async update(dewarId, dewarData) {
    try {
      // Get current dewar to check nitrogen level
      const currentDewar = await databases.getDocument(
        DATABASE_ID,
        COLLECTIONS.DEWARS,
        dewarId
      );

      const previousLevel = parseFloat(currentDewar.liquid_nitrogen_level);
      const newLevel = parseFloat(dewarData.liquidNitrogenLevel);
      const levelChanged = previousLevel !== newLevel;

      // Update dewar
      await databases.updateDocument(
        DATABASE_ID,
        COLLECTIONS.DEWARS,
        dewarId,
        {
          name: dewarData.name,
          location: dewarData.location,
          temperature: dewarData.temp,
          status: dewarData.status,
          liquid_nitrogen_level: newLevel
        }
      );

      // Create history entry if level changed
      if (levelChanged) {
        await databases.createDocument(
          DATABASE_ID,
          COLLECTIONS.NITROGEN_LEVEL_HISTORY,
          ID.unique(),
          {
            dewar_id: dewarId,
            level: newLevel,
            previous_level: previousLevel,
            checked_by: 'Current User',
            action: newLevel > previousLevel ? 'Refilled' : 'Level Check',
            notes: `Level ${newLevel > previousLevel ? 'increased' : 'decreased'} from ${previousLevel}" to ${newLevel}"`,
            checked_at: new Date().toISOString()
          }
        );
      }

      // CRITICAL FIX: Invalidate cache so next getAll() fetches fresh data
      this.invalidateCache();

      return this.getById(dewarId);
    } catch (error) {
      console.error('Error updating dewar:', error);
      throw error;
    }
  },

  /**
   * Delete dewar
   */
  async delete(dewarId) {
    try {
      await databases.deleteDocument(
        DATABASE_ID,
        COLLECTIONS.DEWARS,
        dewarId
      );

      // Note: Cascade delete is handled by Appwrite Function: cascade-delete-dewar

      // CRITICAL FIX: Invalidate cache so next getAll() fetches fresh data
      this.invalidateCache();
    } catch (error) {
      console.error('Error deleting dewar:', error);
      throw error;
    }
  },

  /**
   * Delete box
   */
  async deleteBox(boxId) {
    try {
      await databases.deleteDocument(
        DATABASE_ID,
        COLLECTIONS.BOXES,
        boxId
      );

      // Note: Cascade delete is handled by Appwrite Function: cascade-delete-box

      // CRITICAL FIX: Invalidate cache so next getAll() fetches fresh data
      this.invalidateCache();
    } catch (error) {
      console.error('Error deleting box:', error);
      throw error;
    }
  }
};
